import React, { CSSProperties, Fragment } from "react";
import { Theme } from "../../../../styling/baseui_theme";
import { Card, CardProps, CardOverrides, StyledBody } from "baseui/card";
import { mergeOverrides } from "baseui";
import { Overrides } from "baseui/overrides";
import styled from "styled-components";
import { ButtonProps } from "../../inputs/button";
import { InformationFilled } from "@carbon/icons-react";
import { CarbonIconProps } from "@carbon/icons-react/lib/CarbonIcon";
import { Tooltip } from "../tooltip";

export enum ORIENTATION {
  horizontal = "horizontal",
  vertical = "vertical",
}

export const StyledHorizontalRule = styled.hr`
  background-color: ${({ theme }) => theme.colors.borderOpaque};
  height: 1px;
  border: none;
  width: 100%;
  margin-top: ${({ theme }) => theme.sizing.scale800};
  margin-bottom: ${({ theme }) => theme.sizing.scale800};
`;

const StyledHorizontalDiv = styled.div`
  width: 100%;
  display: flex;
`;

const StyledLeftDiv = styled.div`
  flex: 1;
  padding-right: ${({ theme }) => theme.sizing.scale800};
  vertical-align: middle;
`;

const StyledRightDiv = styled.div`
  flex: 1;
  border-left: ${({ theme }) => theme.colors.borderOpaque} solid 1px;
  padding-left: ${({ theme }) => theme.sizing.scale800};
`;

export type SectionBlockProps = Omit<CardProps, "overrides"> & {
  title?: React.ReactNode;
  titleTooltip?: string | undefined;
  titleTooltipSize?: CarbonIconProps["size"] | undefined;
  bodyTwoPartOrientation?: ORIENTATION[keyof ORIENTATION];
  partOne?: React.ReactNode;
  partTwo?: React.ReactNode;
  partThree?: React.ReactNode;
  button?: ButtonProps;
  footer?: React.ReactNode;
  className?: string | undefined;
  styletronOverrides?: CardOverrides;
  ariaLabel?: string;
  bodyOverrides?: CSSProperties;
  showFooterHorizontalRule?: boolean;
};

const sectionBlockStyleOverride: Overrides<CardOverrides> = {
  Root: {
    style: ({ $theme }: { $theme: Theme }) => ({
      backgroundColor: $theme.colors.white,
      borderTopLeftRadius: "0px",
      borderTopRightRadius: "0px",
      borderBottomRightRadius: "0px",
      borderBottomLeftRadius: "0px",
      paddingTop: $theme.sizing.scale600,
      paddingBottom: $theme.sizing.scale900,
    }),
  },
  Contents: {
    style: () => ({
      position: "relative",
      marginTop: "0px",
      marginBottom: "0px",
      marginLeft: "0px",
      marginRight: "0px",
    }),
  },
  Title: {
    style: ({ $theme }: { $theme: Theme }) => ({
      fontSize: $theme.typography.HeadingXSmall.fontSize,
      lineHeight: $theme.typography.HeadingXSmall.lineHeight,
      fontWeight: $theme.typography.HeadingXSmall.fontWeight,
      paddingLeft: $theme.sizing.scale800,
      paddingRight: $theme.sizing.scale800,
      paddingBottom: $theme.sizing.scale600,
      borderBottom: `${$theme.colors.borderOpaque} solid 1px`,
      verticalAlign: "middle",
    }),
  },
  Body: {
    style: ({ $theme }: { $theme: Theme }) => ({
      marginTop: $theme.sizing.scale800,
      marginLeft: $theme.sizing.scale800,
      marginRight: $theme.sizing.scale800,
      marginBottom: 0,
    }),
  },
};

//A little hacky but this is necessary to support the tabIndex prop which allows the component to be focusable
const StyledBaseUISectionBlock = styled(Card)<SectionBlockProps & { tabIndex?: string }>`
  &:focus,
  &:focus-visible {
    outline-color: ${({ theme }) => theme.colors.accent};
  }
`;
// If we need to add a fourth part to this component in the future, we should consider refactoring.
export const SectionBlock = ({
  title,
  titleTooltip,
  titleTooltipSize = 16,
  bodyTwoPartOrientation,
  partOne,
  partTwo,
  partThree,
  button,
  footer,
  className,
  styletronOverrides,
  bodyOverrides,
  children,
  ariaLabel,
  showFooterHorizontalRule,
  ...props
}: SectionBlockProps) => {
  let styleOverride = sectionBlockStyleOverride;

  if (styletronOverrides) {
    styleOverride = mergeOverrides(
      sectionBlockStyleOverride,
      styletronOverrides as Overrides<CardOverrides>,
    );
  }

  if (!title) {
    styleOverride = mergeOverrides(styleOverride, {
      Body: {
        style: () => ({
          marginTop: 0,
        }),
      },
    } as Overrides<CardOverrides>);
  }

  const fullTitle = titleTooltip ? (
    <Fragment>
      {title}{" "}
      <Tooltip content={titleTooltip}>
        <InformationFilled size={titleTooltipSize} className="info-tooltip" />
      </Tooltip>
    </Fragment>
  ) : (
    title
  );

  let fullBody = children;

  if (bodyTwoPartOrientation === ORIENTATION.vertical) {
    const shouldRenderHorizontalRule = partOne && (partTwo || partThree);
    const shouldRenderAdditionalHorizontalRule = partTwo && partThree;
    fullBody = (
      <Fragment>
        {partOne}
        {shouldRenderHorizontalRule && <StyledHorizontalRule />}
        {partTwo}
        {shouldRenderAdditionalHorizontalRule && <StyledHorizontalRule />}
        {partThree}
      </Fragment>
    );
  } else if (bodyTwoPartOrientation === ORIENTATION.horizontal) {
    fullBody = (
      <Fragment>
        <StyledHorizontalDiv>
          <StyledLeftDiv>{partOne}</StyledLeftDiv>
          <StyledRightDiv>{partTwo}</StyledRightDiv>
          <StyledLeftDiv>{partThree}</StyledLeftDiv>
        </StyledHorizontalDiv>
      </Fragment>
    );
  }

  const fullFooter = (
    <>
      {(!!(partOne || partTwo || partThree) || showFooterHorizontalRule) && (
        <StyledHorizontalRule />
      )}
      {button ? (
        <>
          {footer}
          {button}
        </>
      ) : (
        footer
      )}
    </>
  );

  const defaultBodyOverrides = { marginBottom: 0 };

  const sectionBlock = (
    <StyledBaseUISectionBlock
      title={fullTitle}
      {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
      {...(className?.length ? { className } : {})}
      {...props}
      overrides={styleOverride}
      tabIndex="0"
    >
      <StyledBody
        style={bodyOverrides ? bodyOverrides : defaultBodyOverrides}
        id={"base-styled-body"}
      >
        {fullBody}
      </StyledBody>
      {fullFooter && <>{fullFooter}</>}
    </StyledBaseUISectionBlock>
  );

  return sectionBlock;
};

export const ButtonsContainer = styled.div`
  display: flex;
  justify-content: start;
  flex-direction: column;

  // add margin to the last child
  & > *:nth-child(n + 2):last-child {
    margin-top: ${({ theme }) => theme.sizing.scale600};

    ${({ theme }) => theme.mediaQuery.medium} {
      margin-left: ${({ theme }) => theme.sizing.scale600};
      margin-top: 0;
    }
  }

  ${({ theme }) => theme.mediaQuery.medium} {
    flex-direction: row;
  }
`;
